/**
 * Chat-first home page — an "ask your memory" surface.
 *
 * Three vertically stacked regions on a flex column:
 *   - Sticky header: active-project pill + secondary actions.
 *   - MessageStream: scrollable, fills available space, paginates older
 *     history in as you scroll up, opens pinned to the latest.
 *   - Composer: textarea + send button + slash-palette popover.
 *
 * Capture/forwarding happens in the bot — in the Mini App **anything you
 * send is treated as a question** (ask the memory). Slash commands
 * (/today, /week, /switch, /new project) keep their special handling.
 *
 * State machine:
 *   - `chatReducer` owns the canonical `ChatMessage[]` (sorted ascending,
 *     newest last). The feed is loaded in pages and merged via
 *     `MERGE_FEED` (dedup by id); session asks/digests are appended.
 *
 * Per ADR 0005 chat history is reconstructed on each launch from the
 * feed + session-local /ask exchanges. Nothing persists across tabs.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchFeed } from "../api/feed";
import { askMemory } from "../api/memory";
import { buildDigest } from "../api/digests";
import { fetchMe, setActiveProject } from "../api/users";
import { fetchDashboard } from "../api/dashboard";
import { haptic } from "../hooks/useHaptic";
import { chatReducer, initialChatState } from "./reducer";
import { Composer } from "./Composer";
import { MessageStream } from "./MessageStream";
import type { ParseResult } from "./slash";

// Small page so opening the Mini App doesn't load the whole history.
const FEED_PAGE = 25;
const FEED_POLL_MS = 8_000;

export function ChatHome(): React.ReactElement {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [isSending, setIsSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const navigate = useNavigate();
  // Monotonic counter for client-side message IDs.
  const tempIdSeq = useRef(0);
  const nextTempId = useCallback(() => `tmp-${++tempIdSeq.current}`, []);
  // Cursor (`created_at` of the oldest item fetched so far) for the NEXT
  // older page. Cursors don't shift when new items land at the head — the
  // previous offset scheme did, and could permanently skip a window while
  // the user was scrolling during active capture.
  const olderCursor = useRef<string | null>(null);
  // Once the user pulls an older page, its responses own `hasMore`; the
  // head poll (which always has more below it) must stop overwriting it.
  const hasPaginated = useRef(false);

  const noteOlderCursor = useCallback((cursor: string | null | undefined): void => {
    if (!cursor) return;
    if (
      olderCursor.current === null ||
      new Date(cursor).getTime() < new Date(olderCursor.current).getTime()
    ) {
      olderCursor.current = cursor;
    }
  }, []);

  // ---- Active user + project header data ---------------------------------

  const me = useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 60_000,
  });
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: ({ signal }) => fetchDashboard(signal),
    staleTime: 60_000,
  });

  // ---- Feed: poll the newest page; merge (dedup) into the reducer -------

  const feed = useQuery({
    queryKey: ["feed", "head", FEED_PAGE],
    queryFn: ({ signal }) => fetchFeed(FEED_PAGE, { signal }),
    refetchInterval: FEED_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: FEED_POLL_MS,
  });
  useEffect(() => {
    if (feed.data) {
      dispatch({ type: "MERGE_FEED", items: feed.data.items });
      noteOlderCursor(feed.data.next_cursor);
      // Only the head poll dictates `hasMore` until the user starts
      // pulling older pages (then the older responses take over).
      if (!hasPaginated.current) {
        setHasMore(feed.data.has_more);
      }
    }
  }, [feed.data, noteOlderCursor]);

  // ---- Load the previous (older) page on scroll-up ----------------------

  const loadOlder = useCallback(async (): Promise<void> => {
    if (isLoadingOlder || !hasMore || olderCursor.current === null) return;
    setIsLoadingOlder(true);
    try {
      const resp = await fetchFeed(FEED_PAGE, { before: olderCursor.current });
      dispatch({ type: "MERGE_FEED", items: resp.items });
      hasPaginated.current = true;
      noteOlderCursor(resp.next_cursor);
      setHasMore(resp.has_more);
    } catch {
      // Leave hasMore set so the user can retry the "Load earlier" button.
    } finally {
      setIsLoadingOlder(false);
    }
  }, [hasMore, isLoadingOlder, noteOlderCursor]);

  // ---- Ask flow (also the destination for plain text) -------------------

  const runAsk = useCallback(
    async (question: string): Promise<void> => {
      const id = nextTempId();
      dispatch({ type: "SEND_ASK_QUERY", id, question, now: nowIso() });
      setIsSending(true);
      haptic.impact("light");
      const askPendingId = nextTempId();
      dispatch({
        type: "ADD_PENDING",
        id: askPendingId,
        variant: "thinking",
        now: nowIso(),
      });
      try {
        const ans = await askMemory(question);
        dispatch({ type: "CLEAR_PENDING", id: askPendingId });
        dispatch({
          type: "RESOLVE_ASK_QUERY",
          id,
          answer: {
            ok: true,
            answerId: `ans-${id}`,
            mode: ans.mode,
            content: ans.content,
            citedNoteIds: ans.cited_note_ids,
            now: nowIso(),
          },
        });
        haptic.notify("success");
      } catch {
        dispatch({ type: "CLEAR_PENDING", id: askPendingId });
        dispatch({ type: "RESOLVE_ASK_QUERY", id, answer: { ok: false } });
        haptic.notify("error");
      } finally {
        setIsSending(false);
      }
    },
    [nextTempId],
  );

  // ---- Submit handler dispatching to backends per slash kind ------------

  const handleSubmit = useCallback(
    async (parsed: ParseResult): Promise<void> => {
      if (parsed.type === "incomplete") {
        // The user submitted while the autocomplete was still open —
        // treat as a no-op rather than guess.
        return;
      }
      if (parsed.type === "unknown_command") {
        dispatch({
          type: "APPEND_SYSTEM",
          id: nextTempId(),
          text: `Unknown command: /${parsed.name}`,
          variant: "error",
          now: nowIso(),
        });
        return;
      }
      if (parsed.type === "text") {
        // In the Mini App, plain text is a question — not a note. (Notes
        // are captured by forwarding to the bot.)
        await runAsk(parsed.text);
        return;
      }
      // Command path.
      const cmd = parsed.command;
      switch (cmd.kind) {
        case "ask": {
          await runAsk(cmd.arg);
          return;
        }
        case "today":
        case "week": {
          setIsSending(true);
          const digestPendingId = nextTempId();
          dispatch({
            type: "ADD_PENDING",
            id: digestPendingId,
            variant: "digesting",
            now: nowIso(),
          });
          try {
            const digest = await buildDigest(cmd.kind === "today" ? "day" : "week");
            dispatch({ type: "CLEAR_PENDING", id: digestPendingId });
            dispatch({
              type: "APPEND_DIGEST",
              id: digest.id,
              period: digest.period,
              periodStart: digest.period_start,
              periodEnd: digest.period_end,
              content: digest.content,
              createdAt: digest.created_at,
            });
          } catch {
            dispatch({ type: "CLEAR_PENDING", id: digestPendingId });
            dispatch({
              type: "APPEND_SYSTEM",
              id: nextTempId(),
              text: "Failed to build digest. Try again in a moment.",
              variant: "error",
              now: nowIso(),
            });
          } finally {
            setIsSending(false);
          }
          return;
        }
        case "switch": {
          // V1: client-side name match against the dashboard workspaces.
          // No fuzzy search — case-insensitive substring is enough until
          // we ship a real project picker.
          const projects = (dashboard.data?.workspaces ?? []).flatMap((w) => w.projects);
          const match = projects.find((p) =>
            p.name.toLowerCase().includes(cmd.arg.toLowerCase()),
          );
          if (!match) {
            dispatch({
              type: "APPEND_SYSTEM",
              id: nextTempId(),
              text: `No project matches "${cmd.arg}".`,
              variant: "error",
              now: nowIso(),
            });
            return;
          }
          try {
            await setActiveProject(match.id);
            await me.refetch();
            dispatch({
              type: "APPEND_SYSTEM",
              id: nextTempId(),
              text: `Active project set to "${match.name}".`,
              variant: "info",
              now: nowIso(),
            });
          } catch {
            dispatch({
              type: "APPEND_SYSTEM",
              id: nextTempId(),
              text: "Failed to switch project.",
              variant: "error",
              now: nowIso(),
            });
          }
          return;
        }
        case "new_project": {
          const ws = dashboard.data?.workspaces[0];
          if (!ws) {
            dispatch({
              type: "APPEND_SYSTEM",
              id: nextTempId(),
              text: "Create a workspace from the bot first.",
              variant: "error",
              now: nowIso(),
            });
            return;
          }
          navigate(`/projects/new?workspace=${encodeURIComponent(ws.id)}`);
          return;
        }
      }
    },
    [dashboard.data?.workspaces, me, navigate, nextTempId, runAsk],
  );

  // ---- Header projection -----------------------------------------------

  const activeProjectName = (() => {
    const id = me.data?.active_project_id;
    if (!id || !dashboard.data) return null;
    for (const ws of dashboard.data.workspaces) {
      for (const p of ws.projects) if (p.id === id) return p.name;
    }
    return null;
  })();

  return (
    <div
      className="flex flex-col h-dvh"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <Header
        activeProjectName={activeProjectName}
        onOpenDashboard={() => navigate("/dashboard")}
      />
      <MessageStream
        messages={state.messages}
        isInitialLoading={feed.isLoading && !feed.data}
        hasMore={hasMore}
        isLoadingOlder={isLoadingOlder}
        onLoadOlder={loadOlder}
      />
      <Composer onSubmit={handleSubmit} isSending={isSending} />
    </div>
  );
}

/* ---------- Header ----------------------------------------------------- */

interface HeaderProps {
  activeProjectName: string | null;
  onOpenDashboard: () => void;
}

function Header({
  activeProjectName,
  onOpenDashboard,
}: HeaderProps): React.ReactElement {
  return (
    <header
      className="shrink-0 px-3 py-2.5 flex items-center justify-between gap-2 sticky top-0 z-20"
      style={{
        background: "var(--color-bg)",
        borderBottom: "1px solid var(--surface-hairline)",
      }}
    >
      <div
        className="inline-flex items-center gap-1.5 min-w-0 px-2.5 py-1 rounded-full"
        style={{ background: "var(--color-secondary-bg)" }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: "var(--color-button)" }}
          aria-hidden="true"
        />
        <span
          className="text-[11px] uppercase tracking-wider shrink-0"
          style={{ color: "var(--color-hint)" }}
        >
          {activeProjectName ? "Project" : "Scope"}
        </span>
        <span className="text-[13px] font-medium truncate min-w-0">
          {activeProjectName ?? "All"}
        </span>
      </div>
      <button
        type="button"
        onClick={onOpenDashboard}
        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center active:scale-95 transition-transform"
        style={{
          background: "var(--color-secondary-bg)",
          color: "var(--color-text)",
        }}
        aria-label="Open dashboard"
        title="Dashboard"
      >
        <DashboardIcon />
      </button>
    </header>
  );
}

function DashboardIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function nowIso(): string {
  return new Date().toISOString();
}
